import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { usePartnerRefStore } from '@/stores/partnerRefStore';

/**
 * Partner referral (`?ref=CODE`) handling.
 *
 * The ref is captured from the URL into sessionStorage on first sight and kept
 * for the rest of the browser session so navigation (anchors, footer links, etc.)
 * does not drop partner attribution before signup.
 */
export default function usePartnerRef() {
  const route = useRoute();
  const partnerRefStore = usePartnerRefStore();

  const captureFromQuery = (queryRef) => {
    if (queryRef) {
      partnerRefStore.capture(String(queryRef));
    }
  };

  captureFromQuery(route?.query?.ref);

  watch(
    () => route?.query?.ref,
    (queryRef) => captureFromQuery(queryRef),
    { immediate: true }
  );

  const partnerRef = computed(() => {
    const fromRoute = route?.query?.ref;
    if (fromRoute) {
      return String(fromRoute).trim().toLowerCase();
    }

    if (partnerRefStore.ref) {
      return partnerRefStore.ref;
    }

    if (typeof window !== 'undefined') {
      const fromUrl = new URLSearchParams(window.location.search).get('ref');
      if (fromUrl) {
        partnerRefStore.capture(fromUrl);
        return fromUrl;
      }
    }

    return '';
  });

  const registerTo = computed(() => ({
    path: '/register',
    query: partnerRef.value ? { ref: partnerRef.value } : {}
  }));

  const registerHref = computed(() =>
    partnerRef.value ? `/register?ref=${encodeURIComponent(partnerRef.value)}` : '/register'
  );

  const clearPartnerRef = () => partnerRefStore.clear();

  return { partnerRef, registerTo, registerHref, clearPartnerRef };
}
